import { FC } from 'react';
import styled from 'styled-components';
import { u } from '../common/Units';
import { getReadablePalette } from '../common/Color';
import BlockContainer from './BlockContainer';

/*
 * Shared layout for interactive article blocks (polls and tasks): a card with an image beside the content in
 * landscape and above it in portrait, a question styled like text box titles, and answer buttons.
 */

export const CORRECT_COLOR = "#3E9B4F";
export const WRONG_COLOR = "#E04B4B";

/** Article block wrapper for a task card */
export const TaskBlock = styled(BlockContainer)`
  display: block;
`;

/**
 * Text and control colors that are readable on the page background (the card is only a light tint of it),
 * as CSS variables for the card's parts
 */
const paletteVariables = (backgroundColor?: string): string => {
  const palette = getReadablePalette(backgroundColor);
  return `
    color: ${palette.textColor};
    --task-border: ${palette.isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.3)"};
    --task-control: ${palette.controlColor};
    --task-tint: ${palette.imageBackgroundColor};
    --task-band: ${palette.cardColor};
  `;
};

export const TaskCard = styled.div`
  ${p => paletteVariables(p.theme.backgroundColor)}
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  border-radius: var(--block-radius);
  overflow: hidden;
  background-color: ${p => p.theme.blockColor};

  &.withImage {
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  }

  /* A side image would squeeze the text in portrait */
  @media (orientation: portrait) {
    &.withImage {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`;

/** Fills the height of the content beside it (at least u(320)), but never makes the card taller */
export const TaskCardImage = styled.div`
  position: relative;
  min-height: ${u(320)};
  cursor: pointer;

  & > figure, & > img {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 0;
    object-fit: cover;
  }

  @media (orientation: portrait) {
    min-height: 0;
    height: ${u(380)};
  }
`;

export const TaskCardContent = styled.div`
  padding: ${u(40)};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

/** Small label above the question, e.g. "Task 1 of 3" */
export const TaskEyebrow = styled.div`
  margin-bottom: ${u(10)};
  font-size: 0.95em;
  opacity: 0.75;
`;

/** Same size as the titles of text boxes, so the question reads as the heading of the block */
export const TaskQuestion = styled.h3`
  font-size: 1.3em;
  line-height: 1.25;
  margin: 0 0 ${u(28)};
`;

export const TaskAnswers = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const TaskAnswer = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${u(50)};
  padding: ${u(6)} ${u(20)} ${u(6)} ${u(8)};
  border: 2px solid var(--task-border);
  border-radius: ${u(40)};
  background-color: var(--task-control);
  color: inherit;
  font: inherit;
  font-size: 1.1em;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;

  & + & {
    margin-top: ${u(10)};
  }

  &:active, &.selected {
    border-color: ${p => p.theme.accentColor};
    background-color: ${p => p.theme.accentColor}55;
  }

  &.correct {
    border-color: ${CORRECT_COLOR};
    background-color: ${CORRECT_COLOR}40;
  }

  &.wrong {
    border-color: ${WRONG_COLOR};
    background-color: ${WRONG_COLOR}40;
  }

  &:disabled {
    cursor: default;
  }

  &:disabled:not(.selected):not(.correct):not(.wrong) {
    opacity: 0.6;
  }
`;

/** A, B, C… in front of each answer, which makes long answers easier to scan than centered text */
export const TaskAnswerLetter = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${u(32)};
  height: ${u(32)};
  margin-right: ${u(14)};
  border-radius: 50%;
  background-color: var(--task-tint);
  font-size: 0.8em;
  font-weight: 600;

  .selected > &, .correct > &, .wrong > & {
    color: #fff;
  }

  .selected > & {
    background-color: ${p => p.theme.accentColor};
  }

  .correct > & {
    background-color: ${CORRECT_COLOR};
  }

  .wrong > & {
    background-color: ${WRONG_COLOR};
  }

  & > svg {
    width: 60%;
    height: 60%;
  }
`;

/** Check mark or cross shown in place of the letter once a task is answered */
export const AnswerMark: FC<{ correct: boolean }> = ({ correct }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {correct ? <path d="M5 12.5l4.5 4.5L19 7.5"/> : <path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/>}
  </svg>
);

/** Letter for answer number i (0 → A) */
export const answerLetter = (i: number): string => String.fromCharCode(65 + i);

/** Secondary action, e.g. "View results" */
export const TaskTextButton = styled.button`
  align-self: flex-start;
  min-height: ${u(44)};
  /* The negative bottom margin keeps the tap area without adding space under the text, so the content stays centered */
  margin: ${u(10)} 0 ${u(-14)};
  padding: 0 ${u(4)};
  border: none;
  background: none;
  color: inherit;
  opacity: 0.8;
  font: inherit;
  font-size: 1.05em;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

/** Feedback after answering, as a band across the bottom of the card */
export const TaskFeedback = styled.div`
  grid-column: 1 / -1;
  padding: ${u(24)} ${u(40)};
  border-top: ${u(4)} solid;
  background-color: var(--task-band);
  font-size: 1.1em;
  line-height: 1.4;

  &.correct { border-top-color: ${CORRECT_COLOR}; }
  &.wrong { border-top-color: ${WRONG_COLOR}; }

  & > strong {
    margin-right: 0.4em;
  }
`;

export const TaskNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${u(28)};
`;

/** Main action, e.g. "Next task" */
export const TaskButton = styled.button`
  min-height: ${u(50)};
  padding: ${u(6)} ${u(36)};
  border: none;
  border-radius: ${u(40)};
  background-color: ${p => p.theme.accentColor};
  color: #fff;
  font: inherit;
  font-size: 1.05em;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`;
